import { computed, DestroyRef, effect, Inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DecimalPipe, isPlatformBrowser, Location } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';

import { GetSalesEventV2Request, SalesEvent } from '@proto/store/api/store/home';
import { BaseViewModel } from '../base-view-model';
import { Urls } from '@helper/urls';
import { RouteHelper, RoutePaths } from '@helper/route-paths';
import { Constants } from '@helper/constants';
import { GridItem } from '@common/product-grid/model';
import { SalesEventRepositoryService } from '@service/sales-event-repository.service';
import { WindowDetectService } from '@service/window-detect.service';
import { MetaData } from '@helper/meta-data';

import { Decimal } from 'decimal.js-light';
import { isEqual } from 'lodash-es';
import { map, of, switchMap, throwError } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class SalesEventModel extends BaseViewModel<SalesEvent> {
  // computed
  readonly hasCategories = computed(() => this.apiResult().events.some(x => x.categories.length));
  readonly categories = computed(
    () => this.apiResult().events?.find(e => e.id === this.selectedEventId())?.categories ?? [],
    { equal: (a, b) => isEqual(a, b) }
  );
  readonly selectedEvent = computed(() => this.apiResult().events.find(event => event.id === this.selectedEventId()), {
    equal: (a, b) => isEqual(a, b),
  });
  readonly allItems = computed(
    () =>
      GridItem.toGridItems(
        this.selectedEvent()?.categories.find(category => category.big?.nameEn === this.selectedCategoryName())
          ?.content,
        this.windowDetectService.isMobileDevice(),
        !this.windowDetectService.isMobileDevice()
      ),
    { equal: (a, b) => isEqual(a, b) }
  );
  readonly gridItems = computed(() => this.allItems().slice(0, this.itemCount()), { equal: (a, b) => isEqual(a, b) });
  readonly imageRatio = computed(() => this.categories()[0]?.content?.clothAspectRatio ?? 0);
  // writeable
  readonly selectedEventId = signal('');
  readonly selectedCategoryName = signal('');
  readonly itemCount = signal(Constants.displayCount(this.detectService.isMobileDevice()));
  readonly timestamp = signal(new Date().getTime());
  readonly currentTime = computed(() => {
    if (!this.isBrowser || !this.selectedEvent()) {
      return '';
    }
    const current = this.timestamp();
    const endTime = this.selectedEvent()?.endTime ?? 0;
    if (current >= endTime) {
      return $localize`即將結束`;
    }
    const seconds = new Decimal(endTime).minus(current).div(1000);
    const hour = seconds.div(3600);
    const minute = seconds.mod(3600).div(60).toFixed(0, 3);
    const second = seconds.mod(60).toFixed(0, 3);
    if (hour.lessThan(24)) {
      return `${$localize`距結束`} ${this.decimalPipe.transform(
        hour.toFixed(0, 3),
        '2.0-0'
      )}:${this.decimalPipe.transform(minute, '2.0-0')}:${this.decimalPipe.transform(second, '2.0-0')}`;
    }
    const endDate = new Date(endTime);
    return $localize`截至 ${endDate.getFullYear()}/${this.decimalPipe.transform(
      endDate.getMonth() + 1,
      '2.0-0'
    )}/${this.decimalPipe.transform(endDate.getDate(), '2.0-0')} ${
      this.decimalPipe.transform(endDate.getHours(), '2.0-0') + ':'
    }${this.decimalPipe.transform(endDate.getMinutes(), '2.0-0')}`;
  });

  private readonly isBrowser = isPlatformBrowser(this.platformId);

  scrollToThreshold = false;

  constructor(
    private lativRepository: SalesEventRepositoryService,
    public windowDetectService: WindowDetectService,
    private decimalPipe: DecimalPipe,
    private location: Location,
    @Inject(PLATFORM_ID) private platformId: object
  ) {
    super(SalesEvent.fromPartial({}));
    effect(() => {
      const categories = this.categories();
      if (this.apiResult().events.length && !categories.some(c => c.big?.nameEn === this.selectedCategoryName())) {
        this.selectedCategoryName.set(categories?.[0]?.big?.nameEn ?? '');
      }
    });
    effect(() => {
      const eventId = this.selectedEventId();
      const nameEn = this.selectedCategoryName();
      if ((eventId || nameEn) && this.location.path().includes(RoutePaths.SALES_EVENT)) {
        if (this.location.path().includes(RoutePaths.HOME)) {
          this.location.replaceState(
            `${RouteHelper.homeSalesEvent(eventId, nameEn)}${Urls.pathToParams(location.path())}`
          );
        } else {
          this.location.replaceState(`${RouteHelper.salesEvent(eventId, nameEn)}${Urls.pathToParams(location.path())}`);
        }
        if (this.isBrowser) {
          if (!this.scrollToThreshold) {
            window.scrollTo({ top: 0 });
          }
        }
      }
    });
  }

  getSalesEvent(ref: DestroyRef, eventId?: string, nameEn?: string, adId?: string, adSeq?: number): void {
    if (eventId) {
      this.selectedEventId.set(eventId);
    }
    if (nameEn) {
      this.selectedCategoryName.set(nameEn);
    }
    // 每次都取最新的資料
    this.lativRepository
      .getSalesEventV2(
        GetSalesEventV2Request.fromPartial({
          adId,
          adSeq,
        })
      )
      .pipe(
        switchMap(res => {
          if (!res.events.some(eve => eve.id === this.selectedEventId())) {
            this.selectedEventId.set(res.events[0]?.id || '');
            if (eventId) {
              return throwError(
                () =>
                  new HttpErrorResponse({
                    status: 404,
                    statusText: 'Not Found',
                    error: { message: '優惠活動已結束' },
                  })
              );
            }
          }
          if (
            !res.events
              .find(eve => eve.id === this.selectedEventId())
              ?.categories.some(c => c.big?.nameEn === this.selectedCategoryName())
          ) {
            this.selectedCategoryName.set(res.events[0]?.categories[0]?.big?.nameEn || '');
          }
          return this.selectedEventId()
            ? this.lativRepository
                .getSalesEventV2(
                  GetSalesEventV2Request.fromPartial({
                    eventId: this.selectedEventId(),
                    adId,
                    adSeq,
                  })
                )
                .pipe(map(next => [res, next]))
            : of([res, undefined]);
        }),
        takeUntilDestroyed(ref)
      )
      .subscribe({
        next: res => {
          const target = res[1]?.events.find(e => e.id === this.selectedEventId());
          if (target && res[0]) {
            this.selectedEventId.set(target.id);
            const categoryName = target?.categories.find(
              c => c.big?.nameEn.toUpperCase() === this.selectedCategoryName().toUpperCase()
            )?.big?.nameEn;
            this.selectedCategoryName.set(
              (categoryName || this.selectedCategoryName() || target?.categories[0]?.big?.nameEn) ?? ''
            );
            // 合併活動
            const salesEvent = SalesEvent.fromPartial(res[0]);
            const replaceIndex = salesEvent.events.findIndex(x => x.id === this.selectedEventId());
            if (replaceIndex >= 0) {
              salesEvent.events[replaceIndex] = target;
            }
            this.setApiResult(salesEvent);
          } else if (res[0]) {
            this.setApiResult(res[0]);
          }
        },
        error: err => this.setApiError(err),
      });
  }

  preLoad(ref: DestroyRef): void {
    // 當這個 root model 沒資料時，會打沒 eventId 的全拿 preload.
    // 要注意，當在活動頁，有 adTrackId，就不要打 preload，會影響商品排序.
    this.lativRepository
      .getSalesEventV2(GetSalesEventV2Request.fromPartial({}))
      .pipe(takeUntilDestroyed(ref))
      .subscribe({
        next: res => this.setApiResult(res),
        error: err => this.setApiError(err),
      });
  }

  override getMetaByUrl(segments: string[], model: SalesEvent): MetaData | undefined {
    if (segments[0] === RoutePaths.SALES_EVENT) {
      const event = model.events.find(e => e.id === segments[1]);
      return new MetaData(
        !event ? Constants.PAGE_TITLE : `${event.title}．${event.subTitle}`,
        '',
        model.events[0]?.categories[0]?.content?.items[0]?.listHash
      );
    } else if (segments[1] === RoutePaths.SALES_EVENT) {
      const event = model.events.find(e => e.id === segments[2]);
      return new MetaData(
        !event ? Constants.PAGE_TITLE : `${event.title}．${event.subTitle}`,
        '',
        model.events[0]?.categories[0]?.content?.items[0]?.listHash
      );
    }
    return undefined;
  }
}
